import { useEffect, useRef, useState } from 'react'
import { motionReduced } from '../../lib/usePrefersReducedMotion'

/**
 * A number that counts up once, from the kit's module 05.
 *
 * "Numbers count" is one of the exactly three things `build/DESIGN.md` §3 allows to move, so this
 * is the contract's own motion rather than a landing-page import.
 *
 * **The true value is always in the DOM.** The kit renders every counter at zero and relies on an
 * `IntersectionObserver` to fill it in — which is the shape §3 forbids: a figure that reads "0"
 * forever if the observer never fires is worse than one that never animated. So the number is
 * rendered correct from the first frame, the count is an animation *away from* it and back, and
 * the observer decides only when that happens. If it never fires, a 900ms backstop runs it; if
 * that fails too, the right number was on screen the whole time.
 *
 * Reduced motion drops the animation entirely, which is the swap, not a strip: the figure was
 * already there.
 */
export default function Counted({ value, suffix = '' }: { value: number; suffix?: string }) {
  const ref = useRef<HTMLSpanElement>(null)
  const [shown, setShown] = useState(value)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    // The OS query OR Quil's own in-app toggle. The figure is already correct either way; this
    // only decides whether it counts up to it.
    if (motionReduced()) return

    let raf = 0
    let started = false
    const run = () => {
      if (started) return
      started = true
      const dur = 1100
      let t0: number | null = null
      const frame = (ts: number) => {
        if (t0 === null) t0 = ts
        const t = Math.min(1, (ts - t0) / dur)
        const eased = 1 - Math.pow(1 - t, 3)
        setShown(Math.round(value * eased))
        if (t < 1) raf = requestAnimationFrame(frame)
      }
      setShown(0)
      raf = requestAnimationFrame(frame)
    }

    let io: IntersectionObserver | null = null
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(
        (entries) => {
          if (!entries[0]?.isIntersecting) return
          io?.disconnect()
          run()
        },
        { threshold: 0.6 },
      )
      io.observe(el)
    }
    // The backstop, and the reason the observer is optional rather than load-bearing.
    const backstop = window.setTimeout(run, 900)

    return () => {
      cancelAnimationFrame(raf)
      window.clearTimeout(backstop)
      io?.disconnect()
    }
  }, [value])

  return (
    <span ref={ref}>
      {shown.toLocaleString('en-NZ')}
      {suffix}
    </span>
  )
}
